import React, { useLayoutEffect, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';
import { reduxForm, Field, change, formValueSelector } from 'redux-form';
import { Form, Button, FormGroup, Col, Label } from 'reactstrap';
import {
  clearItemToItinerary,
  getItineraryForCounter,
  clearItinerary,
  getManifestCargoItemsToItinerary,
  getManifestItemToUnloadV2,
  clearCargoLocationGroup,
  postCargoLocationGroup,
  getUserAgenciesV2,
} from '../../../../actions';
import { isRequired } from '../../../../utils/validators';
import { CARGO_OPERATIONS } from '../../../../config/constants';
import { optionsPropTypes } from '../../../common/forms/select/SelectPropTypes';
import { getUniqueLocations } from '../unloading-cargo-from-itinerary/UnloadingCargoForm';
import Select from '../../../common/forms/select/Select';
import FormItem from '../../../common/forms/FormItem';
import ManifestCargo from './manifest-cargo/ManifestCargo';
import AddItemToItinerary from './add-cargo-to-itinerary/AddCargoToItinerary';
import ItinerarySearchInput from '../../../common/forms/input/ItinerarySearchInput';
import CargoOperationsButtons from './CargoOperationsButtons';
import UnloadCargoFromItinerary from './unload-cargo-from-itinerary/UnloadCargoFromItinerary';
import { getManifestItemByItinerary } from '../../../../actions/cargo/ItineraryManifest';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';

const selector = formValueSelector('ManifestForm');

export const ManifestForm = ({
  user,
  handleSubmit,
  agencyOptions,
  loadingAgencies,
  registeredBusId,
  itineraryCounter,
  dispatchChange,
  dispatchClearItinerary,
  dispatchGetUserAgenciesV2,
  dispatchClearItemToItinerary,
  dispatchGetItineraryForCounter,
  dispatchGetManifestItemByItinerary,
  dispatchGetManifestCargoItemsToItinerary,
  dispatchGetManifestItemToUnloadV2,
  dispatchClearCargoLocationGroup,
  dispatchPostCargoLocationGroup,
  cargoLocationGroup,
}) => {
  const [itinerary, setItinerary] = useState(null);
  const [locationIdSelected, setLocationIdSelected] = useState(null);
  const [agenciesToShow, setAgenciesToShow] = useState([]);
  const [operation, setOperation] = useState(null);
  const [listAllItems, setListAllItems] = useState(false);

  useLayoutEffect(
    () => () => {
      dispatchClearItemToItinerary();
      dispatchClearItinerary();
      dispatchClearCargoLocationGroup();
    },
    [],
  );

  useEffect(() => {
    dispatchGetUserAgenciesV2({ userId: user.id });
  }, [user, itinerary]);

  const verifyLocationWithAgencies = () => {
    const uniqueLocations = getUniqueLocations(itineraryCounter, true, true);

    const combinedLocations = new Set([
      ...uniqueLocations.map((location) => location.id),
      ...cargoLocationGroup.map((cargoLocation) => cargoLocation.locationId),
    ]);

    let newAgenciesToShow = [];

    if (combinedLocations.size !== 0) {
      newAgenciesToShow = agencyOptions.filter((agency) =>
        combinedLocations.has(agency.value),
      );

      if (newAgenciesToShow.length === 0) {
        toastr.error(
          'Error',
          'No puede realizar operaciones de carga en este itinerario, ninguna de sus agencias coincide con las paradas.',
        );
        dispatchChange('ManifestForm', 'sourceAgency', '');
        setAgenciesToShow(newAgenciesToShow);
        return;
      }
    }

    dispatchChange('ManifestForm', 'sourceAgency', newAgenciesToShow[0]);

    setAgenciesToShow(newAgenciesToShow);
  };

  const convertLocationToOption = () => {
    const locationsArray = [];

    getUniqueLocations(itineraryCounter, true).forEach((location) => {
      locationsArray.push({
        value: location.id,
        label: location.name,
      });
    });

    return locationsArray;
  };

  const verifyOperationLocation = (currentLocation, unload, add) => {
    if (itineraryCounter.isEmpty()) {
      return true;
    }
    const uniqueLocations = getUniqueLocations(itineraryCounter, unload, add);

    if (uniqueLocations.length !== 0) {
      const combinedLocations = new Set([
        ...uniqueLocations.map((location) => location.id),
        ...cargoLocationGroup.map((cargoLocation) => cargoLocation.locationId),
      ]);

      const verified = combinedLocations.has(currentLocation);

      if (!verified) {
        toastr.error(
          'Error',
          add
            ? 'No puede subir carga en este itinerario, su agencia es la última parada'
            : 'No puede bajar carga de este itinerario, su agencia es la primera parada',
        );
        return verified;
      }

      return verified;
    }

    return false;
  };

  useEffect(() => {
    if (!itineraryCounter.isEmpty()) {
      const locationIds = getUniqueLocations(itineraryCounter, true, true).map(
        (location) => location.id,
      );

      if (locationIds.length > 0) {
        dispatchPostCargoLocationGroup(locationIds);
      }
    }
  }, [itineraryCounter]);

  useEffect(() => {
    if (!itineraryCounter.isEmpty()) {
      verifyLocationWithAgencies();
    }
  }, [cargoLocationGroup]);

  const onHandleSubmit = ({ sourceAgency, destinationAgency }) => {
    const sourceLocationId = sourceAgency.value;

    const destinationLocationId = destinationAgency
      ? destinationAgency.value
      : null;

    switch (operation) {
      case CARGO_OPERATIONS.ADD_ITEM.value:
        if (verifyOperationLocation(sourceLocationId, false, true)) {
          dispatchGetManifestCargoItemsToItinerary({
            itineraryId: itinerary,
            sourceLocationId,
            destinationLocationId,
          });
        }
        break;
      case CARGO_OPERATIONS.UNLOAD_ITEM.value:
        if (verifyOperationLocation(sourceLocationId, true, false)) {
          dispatchGetManifestItemToUnloadV2({
            itineraryId: itinerary,
            registeredBusId: registeredBusId ? registeredBusId.value : null,
            listAllItems,
            sourceAgency: sourceLocationId,
          });
        }
        break;
      case CARGO_OPERATIONS.MANIFEST.value:
        dispatchGetManifestItemByItinerary({ itineraryId: itinerary });
        break;
      default:
        break;
    }

    setLocationIdSelected(sourceAgency.value);
  };

  const handleSelectItinerary = ({ itineraryId }) => {
    setItinerary(itineraryId);
    dispatchGetItineraryForCounter(itineraryId);
  };

  const handleClear = () => {
    setItinerary(null);
    setOperation(null);
    dispatchClearItinerary();
    dispatchClearCargoLocationGroup();
  };

  const handleShowItinerarySearch = () => {
    handleClear();
    dispatchClearItemToItinerary();
    dispatchChange('ManifestForm', 'itineraryId', '');
    dispatchChange('ManifestForm', 'itinerary', '');
  };

  const onClickAddItem = () => {
    setOperation(CARGO_OPERATIONS.ADD_ITEM.value);
  };

  const onClickUnloading = () => {
    setOperation(CARGO_OPERATIONS.UNLOAD_ITEM.value);
  };

  const onClickManifest = () => {
    setOperation(CARGO_OPERATIONS.MANIFEST.value);
  };

  const onClickHandlers = {
    [CARGO_OPERATIONS.ADD_ITEM.value]: onClickAddItem,
    [CARGO_OPERATIONS.UNLOAD_ITEM.value]: onClickUnloading,
    [CARGO_OPERATIONS.MANIFEST.value]: onClickManifest,
  };

  const onChangeSourceAgency = () => {
    setOperation(null);
  };

  useEffect(() => {
    if (registeredBusId === null || registeredBusId === '') {
      setOperation(null);
    } else {
      setAgenciesToShow(agencyOptions);
    }
  }, [registeredBusId]);

  return (
    <>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        {registeredBusId !== null && registeredBusId !== '' ? null : (
          <FormGroup row>
            <FormItem label="Itinerario">
              <Field
                name="itineraryId"
                component={ItinerarySearchInput}
                placeholder="Itinerario"
                handleSelectItinerary={handleSelectItinerary}
                handleShowItinerarySearch={handleShowItinerarySearch}
                handleClear={handleClear}
                props={{
                  form: 'ManifestForm',
                }}
              />
            </FormItem>
          </FormGroup>
        )}
        {itinerary !== null && itinerary ? null : (
          <FormGroup row>
            <FormItem label="Número de Bus">
              <Field
                name="registeredBusId"
                component={RegisteredBusSelect}
                isClearable
                onlyActive
              />
            </FormItem>
          </FormGroup>
        )}
        <FormGroup row>
          <FormItem label="Agencia Actual" required>
            <Field
              name="sourceAgency"
              component={Select}
              isLoading={loadingAgencies}
              options={
                registeredBusId === '' || !!itinerary
                  ? agenciesToShow
                  : agencyOptions
              }
              isDisabled={
                agenciesToShow.length <= 1 &&
                (registeredBusId === '' || !!itinerary)
              }
              onChange={onChangeSourceAgency}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <CargoOperationsButtons
          operations={CARGO_OPERATIONS}
          showNothing={!itinerary && !registeredBusId}
          toShow={
            registeredBusId !== '' && !itinerary
              ? [CARGO_OPERATIONS.UNLOAD_ITEM.value]
              : []
          }
          showAll={!!itinerary}
          onClickHandlers={onClickHandlers}
        />
        {operation === CARGO_OPERATIONS.ADD_ITEM.value ? (
          <FormGroup row>
            <FormItem label="Destino">
              <Field
                name="destinationAgency"
                component={Select}
                isClearable
                options={convertLocationToOption()}
              />
            </FormItem>
          </FormGroup>
        ) : null}

        {operation === CARGO_OPERATIONS.UNLOAD_ITEM.value && (
          <>
            <FormGroup row>
              <FormItem>
                <Label>
                  <Field
                    name="listAllItems"
                    id="listAllItems"
                    component="input"
                    type="checkbox"
                    checked={listAllItems}
                    onChange={() => setListAllItems(!listAllItems)}
                  />{' '}
                  Listar Todas las Encomiendas del Itinerario
                </Label>
              </FormItem>
            </FormGroup>
            <FormGroup row>
              <FormItem>
                <Button type="submit" color="primary">
                  Buscar
                </Button>
              </FormItem>
            </FormGroup>
          </>
        )}

        {operation === CARGO_OPERATIONS.ADD_ITEM.value ? (
          <FormGroup row>
            <Col className="flex row-reverse">
              <Button type="submit" color="primary">
                Buscar
              </Button>
            </Col>
          </FormGroup>
        ) : null}
      </Form>

      {locationIdSelected
        ? (() => {
            switch (operation) {
              case CARGO_OPERATIONS.ADD_ITEM.value:
                return (
                  <AddItemToItinerary
                    itinerary={itinerary}
                    sourceLocationId={locationIdSelected}
                  />
                );
              case CARGO_OPERATIONS.UNLOAD_ITEM.value:
                return (
                  <UnloadCargoFromItinerary
                    itineraryId={itinerary}
                    registeredBusId={
                      registeredBusId ? registeredBusId.value : null
                    }
                    destinationLocationId={locationIdSelected}
                  />
                );
              case CARGO_OPERATIONS.MANIFEST.value:
                return <ManifestCargo itineraryId={itinerary} />;
              default:
                return null;
            }
          })()
        : null}
    </>
  );
};

ManifestForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    customer: PropTypes.shape({
      fullName: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  loadingAgencies: PropTypes.bool,
  agencyOptions: optionsPropTypes,
  itineraryCounter: PropTypes.instanceOf(Immutable.Map).isRequired,
  registeredBusId: PropTypes.instanceOf(Object),
  dispatchChange: PropTypes.func.isRequired,
  dispatchClearItinerary: PropTypes.func.isRequired,
  dispatchGetUserAgenciesV2: PropTypes.func.isRequired,
  dispatchClearItemToItinerary: PropTypes.func.isRequired,
  dispatchGetItineraryForCounter: PropTypes.func.isRequired,
  dispatchGetManifestItemByItinerary: PropTypes.func.isRequired,
  dispatchGetManifestCargoItemsToItinerary: PropTypes.func.isRequired,
  dispatchGetManifestItemToUnloadV2: PropTypes.func.isRequired,
  dispatchClearCargoLocationGroup: PropTypes.func.isRequired,
  dispatchPostCargoLocationGroup: PropTypes.func.isRequired,
  cargoLocationGroup: PropTypes.instanceOf(Object).isRequired,
};

ManifestForm.defaultProps = {
  loadingAgencies: false,
  agencyOptions: [],
  registeredBusId: null,
};

const mapStateToProps = (state) => ({
  user: state.authentication.get('user') || undefined,
  loadingAgencies: state.HumanResourcesUnit.Agency.getIn(['all', 'loading']),
  agencyOptions: state.HumanResourcesUnit.Agency.getIn([
    'all',
    'content',
    'content',
  ]).map((agency) => ({
    value: agency.locationId,
    label: agency.name,
  })),
  itineraryCounter:
    state.ItineraryUnit.Itinerary.getIn(['current', 'content']) || undefined,
  registeredBusId: selector(state, 'registeredBusId'),
  cargoLocationGroup: state.CargoUnit.ManifestCargo.getIn([
    'cargoLocationGroup',
  ]),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchClearItinerary: clearItinerary,
  dispatchGetUserAgenciesV2: getUserAgenciesV2,
  dispatchClearItemToItinerary: clearItemToItinerary,
  dispatchGetItineraryForCounter: getItineraryForCounter,
  dispatchGetManifestItemByItinerary: getManifestItemByItinerary,
  dispatchGetManifestCargoItemsToItinerary: getManifestCargoItemsToItinerary,
  dispatchGetManifestItemToUnloadV2: getManifestItemToUnloadV2,
  dispatchClearCargoLocationGroup: clearCargoLocationGroup,
  dispatchPostCargoLocationGroup: postCargoLocationGroup,
};

const formComponent = reduxForm({
  form: 'ManifestForm',
})(ManifestForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
